import { ArrowUpRight } from 'lucide-react';
import Dot from '@/components/ui/Dot.tsx';
import { cn } from '@/lib/utils';
import { ChangeDraftVm } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { formatDateTime } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { ChangeEditAvailabilityVm } from '@/features/dashboard/bookingChanges/components/editing/editAvailability.ts';

interface ChangeDraftBannerProps {
  draft: ChangeDraftVm;
  isEditing: boolean;
  /**
   * Zdanie o zakresie szkicu doklejane po myślniku, np. „3 pozycje u tego
   * uczestnika, 5 w całej rezerwacji”. Buduje je ekran, bo tylko on wie, ile
   * pozycji szkicu dotyczy akurat jego zakresu.
   */
  scopeLabel?: string;
  /**
   * Dopełniacz nazwy szkicu — „Otwarty szkic {…}”. Ekrany osób mówią
   * o „paczce”, ekran rezerwacji o „paczce zmian”, bo tam nie ma innego
   * kontekstu, który by tłumaczył, czego paczka dotyczy.
   */
  subject?: string;
  /**
   * Paczka czekająca na decyzję klienta zamienia treść belki na powód blokady —
   * projekt W3 mówi wprost, że do czasu odpowiedzi klienta edycja jest zamknięta.
   */
  editLock?: ChangeEditAvailabilityVm;
  onResumeEditing?: () => void;
  /**
   * Zdjęcie paczki czekającej na klienta. Bez tego operator wchodzi w ślepy
   * zaułek: rezerwacja jest zablokowana do edycji, a ekran akceptacji obiecuje
   * mu wprost, że „bez reakcji klienta zdejmie ją sam".
   */
  onWithdrawPackage?: () => void;
  isWithdrawing?: boolean;
  onOpenFullPackage?: () => void;
  /** Slot na przełącznik „Tylko zmienione” — wyłącznie ekran rezerwacji. */
  onlyChangedToggle?: React.ReactNode;
}

const stripClass =
  'flex flex-wrap items-center gap-3.5 border-t px-4 py-3.5 sm:px-7';

/**
 * Metryczka szkicu w belce podglądu. Autor i data ostatniego zapisu bywają
 * puste (paczka założona bez rozpoznanego pracownika, jeszcze nieedytowana),
 * więc segment wypada zamiast zostawiać pustkę między separatorami.
 */
const draftMetaLabel = (draft: ChangeDraftVm): string =>
  [
    draft.author,
    draft.lastSavedAt && `ostatni zapis ${formatDateTime(draft.lastSavedAt)}`,
    'nie wprowadzona, dane poniżej są sprzed zmian.',
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Pasek otwartego szkicu pod zakładkami nagłówka. W podglądzie wygląda jak
 * przypis („dane poniżej są sprzed zmian”), w edycji przechodzi w bursztyn
 * i niesie akcje trybu.
 */
const ChangeDraftBanner = ({
  draft,
  isEditing,
  scopeLabel,
  subject = 'paczki',
  editLock,
  onResumeEditing,
  onWithdrawPackage,
  isWithdrawing,
  onOpenFullPackage,
  onlyChangedToggle,
}: ChangeDraftBannerProps) => {
  const scopeSuffix = scopeLabel ? ` — ${scopeLabel}` : '';
  const isLocked = editLock?.canEdit === false;

  if (isEditing) {
    return (
      <div className={cn(stripClass, 'border-accent-border bg-accent-100')}>
        <div className="flex min-w-0 flex-1 basis-60 items-center gap-3.5">
          <Dot size="lg" />
          <div className="min-w-0">
            <div className="text-[15px] font-bold text-ink">
              {`Edytujesz szkic ${subject}${scopeSuffix}`}
            </div>
            <div className="mt-0.5 text-sm text-neutral-500">
              Zmiany zapisują się do szkicu. Nic nie wejdzie w życie, dopóki nie
              wprowadzisz paczki.
            </div>
          </div>
        </div>
        <div className="ml-auto flex w-full shrink-0 items-center justify-between gap-4 sm:w-auto sm:justify-start">
          {onlyChangedToggle}
          {onOpenFullPackage && (
            <button
              type="button"
              onClick={onOpenFullPackage}
              className="flex min-w-[96px] cursor-pointer items-center justify-center gap-1.5 rounded-[7px] border border-accent-border bg-basic-white px-[11px] py-1.5 text-center text-[13px] font-semibold text-neutral-800 transition-colors hover:border-accent-border-strong"
            >
              Cała paczka
              <ArrowUpRight aria-hidden className="size-3.5 shrink-0" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(stripClass, 'border-neutral-100 bg-neutral-50')}>
      <div className="flex min-w-0 flex-1 basis-60 items-center gap-3.5">
        <Dot size="lg" hollow />
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-ink">
            {isLocked
              ? // Paczka u klienta nie jest już szkicem i nikt jej tu nie wznowi —
                // powód blokady stoi zdanie niżej, więc tytuł mówi, co się z nią stało.
                `Paczka wysłana do akceptacji${scopeSuffix}`
              : `Otwarty szkic ${subject}${scopeSuffix}`}
          </div>
          <div className="mt-0.5 text-sm text-neutral-500">
            {isLocked ? editLock?.reason : draftMetaLabel(draft)}
          </div>
        </div>
      </div>
      {isLocked && onWithdrawPackage && (
        <button
          type="button"
          onClick={onWithdrawPackage}
          disabled={isWithdrawing}
          className="ml-auto w-full shrink-0 cursor-pointer rounded-lg border border-neutral-200 bg-basic-white px-[15px] py-2 text-sm font-bold text-neutral-800 transition-colors hover:border-neutral-300 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          Zdejmij paczkę
        </button>
      )}
      {!isLocked && (
        <button
          type="button"
          onClick={onResumeEditing}
          className="ml-auto w-full shrink-0 cursor-pointer rounded-lg border border-accent-border-strong bg-basic-white px-[15px] py-2 text-sm font-bold text-accent-900 transition-colors hover:bg-accent-100 sm:w-auto"
        >
          Wznów edycję →
        </button>
      )}
    </div>
  );
};

export default ChangeDraftBanner;
